import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiUrl, getAuthHeaders } from '../../config/api.config';

const initialState = {
  messages: [],
  currentMessage: null,
  loading: false,
  error: null,
  success: false,
  submitSuccess: false
};

// Submit message (Public)
export const submitMessage = createAsyncThunk(
  'messages/submit',
  async (messageData, { rejectWithValue }) => {
    try {
      const response = await axios.post(getApiUrl('/messages'), messageData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit message');
    }
  }
);

// Get all messages (Admin)
export const getAllMessages = createAsyncThunk(
  'messages/getAll',
  async ({ status, page = 1, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      let url = `/messages?page=${page}&limit=${limit}`;
      if (status) url += `&status=${status}`;
      const response = await axios.get(getApiUrl(url), { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch messages');
    }
  }
);

// Get single message (Admin)
export const getMessage = createAsyncThunk(
  'messages/getOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(getApiUrl(`/messages/${id}`), { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch message');
    }
  }
);

// Reply to message (Admin)
export const replyMessage = createAsyncThunk(
  'messages/reply',
  async ({ id, replyData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(getApiUrl(`/messages/${id}/reply`), replyData, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send reply');
    }
  }
);

// Update message status (Admin)
export const updateMessageStatus = createAsyncThunk(
  'messages/updateStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await axios.put(getApiUrl(`/messages/${id}/status`), { status }, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update status');
    }
  }
);

// Delete message (Admin)
export const deleteMessage = createAsyncThunk(
  'messages/delete',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(getApiUrl(`/messages/${id}`), { headers: getAuthHeaders() });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete message');
    }
  }
);

const messageSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    clearSuccess: (state) => { state.success = false; },
    clearSubmitSuccess: (state) => { state.submitSuccess = false; },
    clearCurrentMessage: (state) => { state.currentMessage = null; }
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitMessage.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(submitMessage.fulfilled, (state) => { state.loading = false; state.submitSuccess = true; })
      .addCase(submitMessage.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(getAllMessages.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(getAllMessages.fulfilled, (state, action) => { state.loading = false; state.messages = action.payload.data.messages; })
      .addCase(getAllMessages.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(getMessage.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(getMessage.fulfilled, (state, action) => { state.loading = false; state.currentMessage = action.payload.data.message; })
      .addCase(getMessage.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(replyMessage.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(replyMessage.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.messages.findIndex(m => m._id === action.payload.data.message._id);
        if (index !== -1) state.messages[index] = action.payload.data.message;
        state.success = true;
      })
      .addCase(replyMessage.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(updateMessageStatus.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(updateMessageStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.messages.findIndex(m => m._id === action.payload.data.message._id);
        if (index !== -1) state.messages[index] = action.payload.data.message;
        state.success = true;
      })
      .addCase(updateMessageStatus.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(deleteMessage.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(deleteMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = state.messages.filter(m => m._id !== action.payload);
        state.success = true;
      })
      .addCase(deleteMessage.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
  }
});

export const { clearError, clearSuccess, clearSubmitSuccess, clearCurrentMessage } = messageSlice.actions;
export default messageSlice.reducer;
